import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import ButtonGroup from '../../components/ButtonGroup';
import ErrorMessage from '../../components/ErrorMessage';
import FileUploader from '../../components/FileUploader';
import FlirPhotoGallery from '../../components/FlirPhotoGallery';
import PhotoGallery from '../../components/PhotoGallery';
import SimpleButton from '../../components/SimpleButton';
import Spacer from '../../components/Spacer';
import history from '../../providers/history';
import { IApplicationState } from '../../store';
import { itemFlirPhotoSaveRequest, itemPhotoSaveRequest, itemPhotosFlirRequest, itemPhotosRequest } from '../../store/photos/slice';
import { IUploadedInfo } from '../../store/photos/types';
import { sweepRoomItemDraftUpdate, sweepRoomItemRemoveRequest } from '../../store/sweeps/slice';
import { systemAddDialog } from '../../store/system/slice';

const ItemDetails: React.FC = (props) => {
    const route = useMatch('/item/:idSweep/:idRoom/:idItem');

    const { t } = useTranslation();

    const idSweep = route?.params.idSweep;

    const idRoom = route?.params.idRoom;

    const idItem = route?.params.idItem;

    const sweep = useSelector((state: IApplicationState) => state.sweeps.data.find(sweep => sweep.id === idSweep))

    const room = sweep?.rooms.find(room => room.id === idRoom);

    const item = room?.items.find(it => it.id === idItem);

    const [uploadingPhotos, setUploadingPhotos] = useState(false);

    const [uploadingFlirPhotos, setUploadingFlirPhotos] = useState(false);

    const [showFlirPhoto, setShowFlirPhoto] = useState(true);

    const photos = useSelector((state: IApplicationState) => state.photos);

    const dispatch = useDispatch();
    const [flirUpload, setFlirUpload] = useState<{
        normal?: IUploadedInfo,
        flir?: IUploadedInfo,
        itemId?: string,
    }>({
        normal: undefined,
        flir: undefined,
        itemId: undefined
    })

    const flirUploadCallback = useCallback(
        (data: IUploadedInfo, type: 'normal' | 'flir', itemId) => {

            if ((type === 'normal' && flirUpload.flir !== undefined) ||
                (type === 'flir' && flirUpload.normal !== undefined)) {

                if (itemId !== undefined) {
                    dispatch(itemFlirPhotoSaveRequest({
                        uploadNormalId: flirUpload.normal !== undefined ? flirUpload.normal.uploadId : data.uploadId,
                        uploadFlirId: flirUpload.flir !== undefined ? flirUpload.flir.uploadId : data.uploadId,
                        path: flirUpload.normal === undefined ? data.path : flirUpload.normal.path,
                        itemId: itemId,
                        flirPath: flirUpload.flir === undefined ? data.path : flirUpload.flir.path,
                    }))
                }
            }

            else setFlirUpload({
                ...flirUpload,
                [type]: data, itemId
            })
        },
        [flirUpload, dispatch],
    )

    useEffect(() => {
        if (item?.id) {
            dispatch(itemPhotosRequest({
                itemId: item.id
            }))

            dispatch(itemPhotosFlirRequest({
                itemId: item.id
            }))
        }
    }, [dispatch, item?.id])

    if (!item) return <h1>{t("pages.items.not_found")}</h1>;

    if (item.isDeleting) return <h1>{t("pages.items.deleting")}</h1>;

    const itemPhotos = photos.itemPhotos[item.id];

    const itemFlirPhotos = photos.itemFlirPhotos[item.id];

    return (<>
        <h1>{item.name}</h1>

        <p><strong>{t("pages.items.amount")}:</strong> {item.amount ? `${item.amount}` : <em>{t("common.undefined")}</em>}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.items.risk")}:</strong> {item.risk === 2 ? t("pages.items.high") : item.risk === 1 ? t("pages.items.medium") : t("pages.items.low")}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.items.open")}:</strong> {item.open ? t("common.yes") : t("common.no")}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.items.sealed")}:</strong> {item.sealed ? t("common.yes") : t("common.no")}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.items.jeopardized")}:</strong> {item.jeopardized ? t("common.yes") : t("common.no")}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.items.observations")}:</strong> {item.observations ? item.observations : <em>{t("common.undefined")}</em>}</p>
        <Spacer size={5} />
        <p><strong>{t("pages.items.informations")}:</strong> {item.info ? item.info : <em>{t("common.undefined")}</em>}</p>
        <Spacer size={10} />

        <ButtonGroup>
            <SimpleButton size={'small'} prependIcon='pencil' color='primary' onClick={() => {
                dispatch(sweepRoomItemDraftUpdate({
                    ...item,
                    sweepId: sweep?.id
                }));

                history.push(`${item.id}/form`);
            }}>{t("common.buttons.edit")}</SimpleButton>
            <SimpleButton size={'small'} prependIcon='trash' color='danger' onClick={() => {
                dispatch(systemAddDialog({
                    title: t("pages.items.dialogs.remove_item"),
                    message: t("pages.items.dialogs.remove_item_message"),
                    actions: [{
                        text: t("pages.items.buttons.remove_item_confirm"),
                        action: () => {
                            dispatch(sweepRoomItemRemoveRequest(item.id));
                        },
                        color: 'danger',
                        prependIcon: 'trash'
                    },
                    {
                        text: t("pages.items.buttons.remove_item_cancel"),
                        action: () => { },
                        color: 'none'
                    }]
                }));
            }}>{t("common.buttons.delete")}</SimpleButton>
        </ButtonGroup>

        <h1>{t("pages.items.photos")}</h1>

        {itemPhotos && itemPhotos.data.length > 0 && <PhotoGallery type='item' parentId={item.id} sweepId={item.roomId} photos={itemPhotos.data} />}

        {(!itemPhotos || itemPhotos.data.length === 0) && !itemPhotos?.isLoading && <p>{t("pages.items.zero_photos")}</p>}

        <Spacer size={20} />

        {itemPhotos?.error && <>
            <ErrorMessage>{itemPhotos.error.message}</ErrorMessage>
            <Spacer size={20} />
        </>}

        <ButtonGroup>
            {!uploadingPhotos && <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => { setUploadingPhotos(true) }}>{t("pages.rooms.buttons.show_upload")}</SimpleButton>}
            {uploadingPhotos && <SimpleButton size={'small'} prependIcon='minus' color='primary' onClick={() => { setUploadingPhotos(false) }}>{t("pages.rooms.buttons.hide_upload")}</SimpleButton>}
            <SimpleButton loading={itemPhotos?.isLoading} size={'small'} prependIcon='refresh' color='info' onClick={() => {
                dispatch(itemPhotosRequest({
                    itemId: item.id
                }))
            }}>{itemPhotos?.isLoading ? t("pages.items.loading_photos") : ''}</SimpleButton>
        </ButtonGroup>

        <Spacer size={20} />

        {uploadingPhotos && <FileUploader entityId={item.id} type='image' callback={(uploaded) => {

            dispatch(itemPhotoSaveRequest({
                uploadId: uploaded.uploadId,
                itemId: item.id,
                path: uploaded.path
            }))
        }} />}

        <h1>{t("pages.items.flir")}</h1>

        {!showFlirPhoto && (itemFlirPhotos && itemFlirPhotos.data.length > 0 && <FlirPhotoGallery photos={itemFlirPhotos.data} parentId={item.id} type={'item'} typePhoto={'normal'} />)}
        {showFlirPhoto && (itemFlirPhotos && itemFlirPhotos.data.length > 0 && <FlirPhotoGallery photos={itemFlirPhotos.data} parentId={item.id} type={'item'} typePhoto={'flir'} />)}

        {(!itemFlirPhotos || itemFlirPhotos.data.length === 0) && !itemFlirPhotos?.isLoading && <p>{t("pages.items.zero_flir")}</p>}

        <Spacer size={20} />

        <ButtonGroup>

            {!uploadingFlirPhotos && <SimpleButton size={'small'} prependIcon='plus' color='primary' onClick={() => { setUploadingFlirPhotos(true) }}>{t("pages.items.buttons.add_flir")}</SimpleButton>}
            {uploadingFlirPhotos && <SimpleButton size={'small'} prependIcon='minus' color='primary' onClick={() => { setUploadingFlirPhotos(false) }}>{t("pages.items.buttons.hide_flir")}</SimpleButton>}

            {!showFlirPhoto && <SimpleButton size={'small'} prependIcon='photo' color='secondary' onClick={() => { setShowFlirPhoto(true) }}>{t("pages.items.buttons.show_photo_flir")}</SimpleButton>}
            {showFlirPhoto && <SimpleButton size={'small'} prependIcon='photo' color='secondary' onClick={() => { setShowFlirPhoto(false) }}>{t("pages.items.buttons.show_photo_normal")}</SimpleButton>}

            <SimpleButton loading={itemFlirPhotos?.isLoading} size={'small'} prependIcon='refresh' color='info' onClick={() => {
                dispatch(itemPhotosFlirRequest({
                    itemId: item.id
                }))
            }}>{itemFlirPhotos?.isLoading ? t("pages.items.loading_flir") : ''}</SimpleButton>
        </ButtonGroup>

        <Spacer size={15} />

        {uploadingFlirPhotos && <strong>{t("pages.items.uploading_normal")}</strong>}

        <Spacer size={10} />

        {uploadingFlirPhotos && <FileUploader type={'image'} entityId={item.id} callback={(uploaded) => {
            flirUploadCallback(uploaded, 'normal', item.id);
        }} />}

        <Spacer size={10} />

        {uploadingFlirPhotos && <strong>{t("pages.items.uploading_flir")}</strong>}

        <Spacer size={10} />

        {uploadingFlirPhotos && <FileUploader type={'image'} entityId={item.id} callback={(uploaded) => {
            flirUploadCallback(uploaded, 'flir', item.id);
        }} />}
    </>);
}

export default ItemDetails;